import React, { useState, useEffect, useCallback, useRef } from "react";
import { apiCall } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Play, Square, Phone, Search, Flame, UserPlus, Users, CheckCircle, XCircle, Loader2 } from "lucide-react";

export default function AutoDialerPage() {
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [selectedClients, setSelectedClients] = useState([]);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [dialing, setDialing] = useState(false);
  const [currentClient, setCurrentClient] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState([]);
  const dialingRef = useRef(false);
  const queueRef = useRef([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, limit: 500 });
      if (category !== "all") params.set("category", category);
      if (search) params.set("search", search);
      const [cr, ar] = await Promise.all([apiCall("get", `/clients?${params}`), apiCall("get", "/agents")]);
      setClients(cr.data.clients.filter(c => c.last_call_response !== "converted" && c.last_call_response !== "not_interested"));
      setAgents(ar.data.filter(a => a.is_active));
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, [category, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleClient = (id) => setSelectedClients(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const selectAll = () => setSelectedClients(prev => prev.length === clients.length ? [] : clients.map(c => c.user_id));

  const startDialer = () => {
    if (!selectedAgent) { toast.error("Select agent"); return; }
    if (!selectedClients.length) { toast.error("Select clients"); return; }
    setDialing(true);
    dialingRef.current = true;
    setResults([]);
    queueRef.current = [...selectedClients];
    setCurrentIndex(0);
    dialNext(0);
  };

  const dialNext = async (idx) => {
    const q = queueRef.current;
    if (!dialingRef.current || idx >= q.length) {
      setDialing(false);
      dialingRef.current = false;
      if (idx >= q.length) toast.success("Auto dialer completed!");
      return;
    }
    const cid = q[idx];
    setCurrentIndex(idx);
    setCurrentClient(clients.find(c => c.user_id === cid) || { user_id: cid });

    try {
      const { data } = await apiCall("post", "/bonvoice/auto-dialer", { client_ids: [cid], agent_id: selectedAgent });
      toast.info(`Calling ${cid} - Agent will get popup...`);
      setResults(prev => [...prev, { user_id: cid, status: "calling", event_id: data.event_id }]);

      // Wait for agent to respond (poll call log for response)
      await waitForResponse(data.event_id, cid, idx);
    } catch (err) {
      setResults(prev => [...prev, { user_id: cid, status: "error" }]);
      toast.error(`Call to ${cid} failed`);
      if (dialingRef.current) setTimeout(() => dialNext(idx + 1), 2000);
    }
  };

  const waitForResponse = async (eventId, cid, idx) => {
    // Poll every 3s for up to 2 minutes for agent to submit response
    let attempts = 0;
    const maxAttempts = 40; // 40 * 3s = 120s
    const poll = async () => {
      if (!dialingRef.current) return;
      attempts++;
      try {
        const { data } = await apiCall("get", `/call-logs?page=1&limit=1`);
        const log = data.logs.find(l => l.call_id === eventId);
        if (log && log.response) {
          // Agent responded, update result and move to next
          setResults(prev => prev.map(r => r.event_id === eventId ? { ...r, status: "done", response: log.response } : r));
          toast.success(`${cid}: ${log.response}`);
          if (dialingRef.current) setTimeout(() => dialNext(idx + 1), 1500);
          return;
        }
        if (log && (log.status === "Not Connected" || log.status === "Call Cut")) {
          setResults(prev => prev.map(r => r.event_id === eventId ? { ...r, status: log.status } : r));
          if (dialingRef.current) setTimeout(() => dialNext(idx + 1), 1500);
          return;
        }
      } catch {}
      if (attempts < maxAttempts && dialingRef.current) {
        setTimeout(poll, 3000);
      } else if (dialingRef.current) {
        // Timeout - skip
        setResults(prev => prev.map(r => r.event_id === eventId ? { ...r, status: "timeout" } : r));
        dialNext(idx + 1);
      }
    };
    setTimeout(poll, 3000);
  };

  const stopDialer = () => { setDialing(false); dialingRef.current = false; toast.info("Stopped"); };

  const getCatBadge = (cat) => {
    if (cat === "new") return <Badge className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 text-xs"><UserPlus className="w-3 h-3 mr-1" />New</Badge>;
    if (cat === "hot") return <Badge className="bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20 text-xs"><Flame className="w-3 h-3 mr-1" />Hot</Badge>;
    return <Badge variant="secondary" className="text-xs"><Users className="w-3 h-3 mr-1" />Regular</Badge>;
  };

  const getResultIcon = (status) => {
    if (status === "calling") return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
    if (status === "done") return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (status === "error" || status === "Not Connected" || status === "Call Cut") return <XCircle className="w-4 h-4 text-red-500" />;
    return <XCircle className="w-4 h-4 text-gray-400" />;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="auto-dialer-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Auto Dialer</h2>
          <p className="text-sm text-muted-foreground mt-1">{dialing ? `Calling ${currentIndex + 1}/${queueRef.current.length} - Agent receives popup` : "Select leads and agent"}</p>
        </div>
      </div>

      <Card className="border border-border"><CardContent className="p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div><label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">Agent</label>
            <Select value={selectedAgent} onValueChange={setSelectedAgent}><SelectTrigger className="w-48" data-testid="auto-dialer-agent-select"><SelectValue placeholder="Choose agent..." /></SelectTrigger><SelectContent>{agents.map(a => <SelectItem key={a._id} value={a._id}>{a.name} (Ext: {a.extension})</SelectItem>)}</SelectContent></Select>
          </div>
          <div><label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">Category</label>
            <Select value={category} onValueChange={setCategory}><SelectTrigger className="w-32"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="new">New</SelectItem><SelectItem value="hot">Hot</SelectItem><SelectItem value="regular">Regular</SelectItem></SelectContent></Select>
          </div>
          <div><label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">Search</label>
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input placeholder="User ID" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-40" /></div>
          </div>
          {!dialing ? (
            <Button onClick={startDialer} disabled={!selectedAgent || !selectedClients.length} data-testid="start-dialer-btn"><Play className="w-4 h-4 mr-2" /> Start ({selectedClients.length})</Button>
          ) : (
            <Button variant="destructive" onClick={stopDialer} data-testid="stop-dialer-btn"><Square className="w-4 h-4 mr-2" /> Stop</Button>
          )}
        </div>
      </CardContent></Card>

      {/* Progress */}
      {dialing && currentClient && (
        <Card className="border border-primary/30 bg-primary/5"><CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="animate-pulse"><Phone className="w-8 h-8 text-primary" /></div>
            <div className="flex-1">
              <p className="text-sm font-semibold">Calling: {currentClient.user_id}</p>
              <p className="text-xs text-muted-foreground">Waiting for agent response... (popup on agent's screen)</p>
              <div className="mt-2 bg-muted rounded-full h-2 overflow-hidden"><div className="bg-primary h-full transition-all" style={{ width: `${((currentIndex + 1) / queueRef.current.length) * 100}%` }} /></div>
            </div>
            <p className="text-lg font-bold">{currentIndex + 1}/{queueRef.current.length}</p>
          </div>
        </CardContent></Card>
      )}

      {/* Results */}
      {results.length > 0 && (
        <Card className="border border-border"><CardContent className="p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Results</p>
          <div className="space-y-1">
            {results.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                {getResultIcon(r.status)}
                <span className="font-medium">{r.user_id}</span>
                <span className="text-muted-foreground capitalize">{r.status}{r.response ? ` - ${r.response}` : ""}</span>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}

      {/* Client Selection */}
      <Card className="border border-border"><CardContent className="p-0">
        <div className="p-3 border-b border-border flex items-center justify-between">
          <p className="text-sm font-semibold">{selectedClients.length}/{clients.length} selected</p>
          <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">{selectedClients.length === clients.length ? "Deselect All" : "Select All"}</Button>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead className="w-10"></TableHead><TableHead className="text-xs uppercase">User ID</TableHead><TableHead className="text-xs uppercase">Category</TableHead><TableHead className="text-xs uppercase">Response</TableHead><TableHead className="text-xs uppercase">Calls</TableHead></TableRow></TableHeader>
          <TableBody>
            {clients.map((c, i) => (
              <TableRow key={i} className={selectedClients.includes(c.user_id) ? "bg-primary/5" : ""}>
                <TableCell><Checkbox checked={selectedClients.includes(c.user_id)} onCheckedChange={() => toggleClient(c.user_id)} /></TableCell>
                <TableCell className="font-medium text-sm">{c.user_id}</TableCell>
                <TableCell>{getCatBadge(c.category)}</TableCell>
                <TableCell className="text-sm capitalize">{c.last_call_response || "-"}</TableCell>
                <TableCell className="text-sm">{c.call_count || 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
